import React, { useState, useEffect } from 'react';
import { X, UserPlus, Check, XCircle } from 'lucide-react';

const FriendRequestsModal = ({ isOpen, onClose }) => {
  const [friendRequests, setFriendRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  // Load friend requests
  const loadFriendRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/friends/requests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFriendRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error loading friend requests:', error);
    } finally {
      setLoading(false);
    }
  };

  // Accept friend request
  const acceptRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/friends/requests/${requestId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log('✅ Friend request accepted');
        // Remove from list
        setFriendRequests(prev => prev.filter(req => req.id !== requestId));
      } else {
        alert('Failed to accept friend request');
      }
    } catch (error) {
      console.error('Error accepting request:', error);
      alert('Error accepting friend request');
    }
  };

  // Reject friend request
  const rejectRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/friends/requests/${requestId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log('❌ Friend request rejected');
        // Remove from list
        setFriendRequests(prev => prev.filter(req => req.id !== requestId));
      } else {
        alert('Failed to reject friend request');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error rejecting friend request');
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadFriendRequests();
      // Poll for new requests
      const interval = setInterval(loadFriendRequests, 10000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <div className="bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900 rounded-2xl border border-purple-500/30 w-full max-w-md max-h-[600px] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
          <div className="flex items-center gap-2">
            <UserPlus size={24} className="text-purple-400" />
            <div>
              <h3 className="text-white font-semibold text-lg">Friend Requests</h3>
              {friendRequests.length > 0 && (
                <p className="text-xs text-gray-400">{friendRequests.length} pending</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800/50 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        {/* Requests List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : friendRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <UserPlus size={48} className="text-gray-600 mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">No pending requests</h4>
              <p className="text-gray-400 text-sm">When someone sends you a friend request, it will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {friendRequests.map((request) => (
                <div key={request.id} className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                        {request.from_user_avatar ? (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${request.from_user_avatar.replace('/uploads/', '/api/uploads/')}`} 
                            alt={request.from_user_name} 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          request.from_user_name?.charAt(0).toUpperCase() || 'U'
                        )}
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm">{request.from_user_name || 'Unknown User'}</h4>
                        <p className="text-xs text-gray-400">{request.from_user_email}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => acceptRequest(request.id)}
                      className="flex-1 bg-green-600/20 hover:bg-green-600/30 border border-green-500/50 text-green-300 hover:text-white px-3 py-2 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <Check size={16} />
                      Accept
                    </button>
                    <button
                      onClick={() => rejectRequest(request.id)}
                      className="flex-1 bg-red-600/20 hover:bg-red-600/30 border border-red-500/50 text-red-300 hover:text-white px-3 py-2 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <XCircle size={16} />
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendRequestsModal;
